import React, { memo, useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Chip,
  Popper,
} from '@mui/material';
import {
  Send as SendIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLazyListAccountMasterv2Query } from '../../../features/AccountsAndGroups/api/accountMaster.api';
import { AccountMasterListResponse } from '../../../types/graphql';

interface ChatInputProps {
  inputMessage: string | undefined;
  setInputMessage: (message: string) => void;
  onSendMessage: () => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  isConnected: boolean;
  isLoading: boolean;
}

interface AccountSuggestion {
  accountsid?: number | null;
  accountname?: string | null;
  accounttypecd?: string | null;
  accountgroupname?: string | null;
}

export const ChatInputWithAutocomplete = memo<ChatInputProps>(({
  inputMessage,
  setInputMessage,
  onSendMessage,
  onKeyPress,
  isConnected,
  isLoading,
}) => {
  const theme = useTheme();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<AccountSuggestion[]>([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [atPosition, setAtPosition] = useState(-1);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [triggerSearch, { data: accountData, isLoading: isSearching }] = useLazyListAccountMasterv2Query();

  // Debug logging
  useEffect(() => {
    if (accountData) {
      console.log('Account data loaded:', accountData.length, 'accounts');
    }
  }, [accountData]);

  // Extract @/ pattern and search term (Cursor-style)
  const extractAtPattern = (text: string, cursorPos: number) => {
    const beforeCursor = text.substring(0, cursorPos);
    const atMatch = beforeCursor.match(/@\/([^@\s]*)$/);
    
    if (atMatch) {
      console.log('At pattern found:', {
        searchTerm: atMatch[1],
        fullMatch: atMatch[0],
        cursorPos,
        startPos: cursorPos - atMatch[0].length,
        endPos: cursorPos
      });
      return {
        searchTerm: atMatch[1],
        startPos: cursorPos - atMatch[0].length,
        endPos: cursorPos
      };
    }
    return null;
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    console.log('Input change:', value, 'cursor:', cursorPos);
    
    setInputMessage(value);
    setCursorPosition(cursorPos);

    // Clear previous timeout
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }

    const atPattern = extractAtPattern(value, cursorPos);
    
        if (atPattern) {
          setAtPosition(atPattern.startPos);
          setSearchTerm(atPattern.searchTerm);
          
          // Debounce search
          suggestionTimeoutRef.current = setTimeout(() => {
            if (atPattern.searchTerm.length >= 1) {
              // Only trigger search if we haven't fetched data yet
              if (!hasFetchedData) {
                triggerSearch({});
                setHasFetchedData(true);
              }
              setShowSuggestions(true);
            } else {
              setShowSuggestions(false);
            }
          }, 150);
        } else {
          setShowSuggestions(false);
          setSuggestions([]);
          setAtPosition(-1);
          setSearchTerm('');
        }
  };

      // Update suggestions when data changes - filter client-side
      useEffect(() => {
        if (accountData && showSuggestions) {
          let filteredAccounts = accountData;
          
            // Filter accounts client-side based on search term if provided
            if (searchTerm && searchTerm.length > 0) {
              const searchLower = searchTerm.toLowerCase();
              filteredAccounts = accountData.filter((account) => {
                const nameMatch = account?.accountname?.toLowerCase().includes(searchLower);
                const typeMatch = account?.accounttypecd?.toLowerCase().includes(searchLower);
                const groupMatch = account?.accountgroupname?.toLowerCase().includes(searchLower);
                return nameMatch || typeMatch || groupMatch;
              });
            }
          
          const accountSuggestions: AccountSuggestion[] = filteredAccounts
            .filter((account): account is NonNullable<typeof account> => account != null)
            .map((account) => ({
              accountsid: account.accountsid,
              accountname: account.accountname,
              accounttypecd: account.accounttypecd,
              accountgroupname: account.accountgroupname,
            }));
          
          console.log('Filtered suggestions:', accountSuggestions.length, 'for search:', searchTerm);
          setSuggestions(accountSuggestions);
          setSelectedIndex(0); // Reset selection
        } else if (!showSuggestions) {
          setSuggestions([]);
        }
      }, [accountData, showSuggestions, searchTerm]);

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: AccountSuggestion) => {
    if (atPosition >= 0) {
      const beforeAt = inputMessage?.substring(0, atPosition) || '';
      const afterCursor = inputMessage?.substring(cursorPosition) || '';
      
      // Insert the selected account in Cursor style: @/AccountName(AccountID)
      const replacement = `@/${suggestion.accountname}(${suggestion.accountsid})`;
      const newMessage = beforeAt + replacement + afterCursor;
      
      console.log('Selection debug:', {
        beforeAt,
        afterCursor,
        replacement,
        newMessage,
        atPosition,
        cursorPosition
      });
      
      setInputMessage(newMessage);
      setShowSuggestions(false);
      setSuggestions([]);
      setAtPosition(-1);
      setSearchTerm('');
      
      // Focus back to input and position cursor after the replacement
      setTimeout(() => {
        inputRef.current?.focus();
        const newCursorPos = beforeAt.length + replacement.length;
        inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSuggestions([]);
        setAtPosition(-1);
        setSearchTerm('');
      } else if (e.key === 'Tab') {
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
      }
    } else {
      // Call original onKeyPress for other keys
      onKeyPress(e);
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSuggestions([]);
        setAtPosition(-1);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ position: 'relative' }} ref={anchorRef}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          ref={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask something..."
          value={inputMessage || ''}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={!isConnected}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
        <IconButton
          onClick={onSendMessage}
          disabled={!inputMessage?.trim() || !isConnected || isLoading}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabled,
              color: theme.palette.action.disabled,
            },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      {/* Cursor-style Suggestions Dropdown */}
      <Popper
        open={showSuggestions && suggestions.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ 
          width: anchorRef.current?.offsetWidth, 
          zIndex: 1300,
          maxWidth: '400px'
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <Paper
          elevation={12}
          sx={{
            maxHeight: 280,
            overflow: 'hidden',
            border: '2px solid #1976d2',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            backgroundColor: '#ffffff !important',
            color: '#000000 !important',
            '& *': {
              color: '#000000 !important',
            },
          }}
        >
          {/* Header */}
          <Box sx={{ 
            p: 1, 
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5 !important',
            color: '#000000 !important',
          }}>
            <Typography variant="caption" sx={{ color: '#666666 !important' }}>
              Accounts matching "{searchTerm}"
            </Typography>
          </Box>
          
          <List dense sx={{ py: 0 }}>
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={suggestion.accountsid}
                button
                selected={index === selectedIndex}
                onClick={() => handleSuggestionSelect(suggestion)}
                sx={{
                  py: 1,
                  px: 2,
                  color: '#000000 !important',
                  backgroundColor: '#ffffff !important',
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd !important',
                    color: '#1976d2 !important',
                    '&:hover': {
                      backgroundColor: '#e3f2fd !important',
                      color: '#1976d2 !important',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#f5f5f5 !important',
                    color: '#000000 !important',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ color: '#000000 !important' }}>
                        {suggestion.accountname}
                      </Typography>
                      {suggestion.accounttypecd && (
                        <Chip
                          label={suggestion.accounttypecd}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.75rem', 
                            height: 20,
                            backgroundColor: theme.palette.background.paper
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    suggestion.accountgroupname && (
                      <Typography variant="caption" sx={{ color: '#666666 !important' }}>
                        {suggestion.accountgroupname}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
            
                {isSearching && (
                  <ListItem sx={{ py: 1, px: 2, color: '#000000 !important' }}>
                    <ListItemText 
                      primary={
                        <Typography variant="body2" sx={{ color: '#666666 !important' }}>
                          Searching accounts...
                        </Typography>
                      } 
                    />
                  </ListItem>
                )}
            
            {!isSearching && suggestions.length === 0 && showSuggestions && searchTerm && (
              <ListItem sx={{ py: 1, px: 2, color: '#000000 !important' }}>
                <ListItemText 
                  primary={
                    <Typography variant="body2" sx={{ color: '#666666 !important' }}>
                      No accounts found for "{searchTerm}"
                    </Typography>
                  } 
                />
              </ListItem>
            )}
          </List>
          
          {/* Footer with keyboard hints */}
          {showSuggestions && suggestions.length > 0 && (
            <Box sx={{ 
              p: 1, 
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5 !important',
              color: '#000000 !important',
            }}>
              <Typography variant="caption" sx={{ color: '#666666 !important' }}>
                ↑↓ to navigate • Enter/Tab to select • Esc to close
              </Typography>
            </Box>
          )}
        </Paper>
      </Popper>
    </Box>
  );
});

ChatInputWithAutocomplete.displayName = 'ChatInputWithAutocomplete';
